/**
 * Who wrote a chat turn.
 *
 * The header label used to be derived from live context — `useAuth().user` for
 * every question and `useSession().selectedPersona` for every answer. That made
 * the attribution a property of the *viewer* rather than of the turn: a shared
 * thread showed every reader their own name on someone else's question, and
 * picking a different persona retroactively relabelled every past answer.
 *
 * Attribution now travels with the message (migration 009 —
 * `conversation_messages.author_user_id` / `author_name`), and this module is
 * the single place that turns it into a label. Pure, so it is covered without a
 * DOM (vitest runs in the `node` environment for this repo).
 *
 * @typedef {{ kind: 'self' }
 *   | { kind: 'user', name: string }
 *   | { kind: 'persona', name: string }
 *   | { kind: 'tutor' }} AuthorDescriptor
 */

/**
 * @param {object} message - a chat turn (`role`, optional `author`, `persona`).
 * @param {{ id?: string }|null} [currentUser] - the viewer, if signed in.
 * @returns {AuthorDescriptor}
 */
export function resolveAuthor(message, currentUser = null) {
  if (message?.role === 'user') {
    const author = message.author || {};
    // Identity is matched on id, never on the display name: two tenants can
    // both have an "admin", and a rename must not orphan someone's history.
    if (author.id && currentUser?.id && author.id === currentUser.id) return { kind: 'self' };
    if (author.name) return { kind: 'user', name: author.name };
    // Pre-009 rows recorded no author at all. Fall through to the neutral
    // label — it names nobody, so it cannot name the wrong person.
    return { kind: 'self' };
  }

  // An answer is authored by the persona that produced it, if any. `persona`
  // is what the live turn carries; `author.name` is what the row carries after
  // a reload. `author.id` is meaningless here — a user never authors an answer.
  const name = message?.persona?.name || message?.author?.name;
  return name ? { kind: 'persona', name } : { kind: 'tutor' };
}

/**
 * Whether a turn was written by the AI.
 *
 * Art. 50 Abs. 1 AI Act (in force since 2026-08-02) requires every answer to be
 * recognisable as AI-generated. Persona answers are the reason this is decided
 * on the descriptor kind and never on the name: "Alex Vargas" reads like a
 * person, and that is exactly the impression the marker has to correct.
 *
 * @param {AuthorDescriptor} descriptor
 * @returns {boolean}
 */
export function isAiAuthored(descriptor) {
  return descriptor?.kind === 'persona' || descriptor?.kind === 'tutor';
}

/**
 * @param {AuthorDescriptor} descriptor
 * @param {(key: string) => string} t - i18next translator.
 * @returns {string}
 */
export function authorLabel(descriptor, t) {
  switch (descriptor.kind) {
    case 'user':
    case 'persona':
      return descriptor.name;
    case 'tutor':
      return t('chat.tutor');
    case 'self':
    default:
      return t('chat.you');
  }
}
